"use client";


import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { HeroHeader } from "@/components/marketing/2026/mist-hero/header"
import { Footer } from "@/components/marketing/2026"
import { Zap } from "lucide-react";
import { TableOfContents } from "./toc";
import { DocsSidebar } from "./docs-sidebar"

interface LegalLayoutProps {
    children: React.ReactNode
    // toc prop removed as it's now client-side
    title: string
    lastUpdated: string
    locale: string
}

import { getAuthUrl } from "@/lib/auth-urls";

export function LegalLayout({ children, title, lastUpdated, locale }: LegalLayoutProps) {
    const isRtl = locale === 'ar';
    const dir = isRtl ? 'rtl' : 'ltr';

    // Define navigation items dynamically based on locale
    const navItems = [
        { title: isRtl ? 'شروط الخدمة' : 'Terms of Service', href: `/${locale}/legal/terms` },
        { title: isRtl ? 'سياسة الخصوصية' : 'Privacy Policy', href: `/${locale}/legal/privacy` },
        { title: isRtl ? 'اتفاقية التاجر' : 'Merchant Agreement', href: `/${locale}/legal/merchant-agreement` },
        { title: isRtl ? 'سياسة الاستخدام المقبول' : 'Acceptable Use Policy', href: `/${locale}/legal/aup` },
        { title: isRtl ? 'سياسة الاسترجاع' : 'Refund Policy', href: `/${locale}/legal/refund` },
    ];

    return (
        <div className="flex flex-col min-h-screen bg-background" dir={dir}>
            <HeroHeader />

            <div className="relative mx-2 my-2 rounded-2xl md:rounded-[2rem] bg-muted/50 border border-border/50 min-h-[calc(100vh-100px)]">
                <div className="mx-auto w-full max-w-[90rem] flex-1 px-4 sm:px-6 md:px-8 py-10 md:py-20 pt-16 md:pt-24">
                    <div className="flex flex-col lg:flex-row gap-10 lg:gap-12 xl:gap-20 relative">
                        {/* Left Sidebar: Navigation (Sticky) */}
                        <aside className="hidden lg:block w-64 shrink-0 transition-all">
                            <div className="sticky top-28 max-h-[calc(100vh-140px)] overflow-y-auto">
                                <DocsSidebar items={navItems} locale={locale} />
                            </div>
                        </aside>

                        {/* Middle: Main Content */}
                        <main className="flex-1 min-w-0 max-w-4xl mx-auto lg:mx-0">
                            {/* Breadcrumbs Simulation */}
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                                <span>{isRtl ? 'المركز القانوني' : 'Legal Center'}</span>
                                <span className="text-border">/</span>
                                <span className="font-medium text-primary">{title}</span>
                            </div>

                            <div className="mb-10 border-b border-border/40 pb-6">
                                <h1 className="text-4xl font-bold tracking-tight lg:text-4xl mb-3">
                                    {title}
                                </h1>
                                <p className="text-sm text-muted-foreground">
                                    {isRtl ? 'آخر تحديث:' : 'Last Updated:'} <span className="font-medium text-foreground">{lastUpdated}</span>
                                </p>
                            </div>

                            {/* Hybrid Content Area - Exact Nextra Emulation */}
                            <article className="prose prose-slate dark:prose-invert max-w-none 
                                                prose-headings:scroll-m-20 prose-headings:tracking-tight
                                                prose-h1:text-4xl prose-h1:font-bold prose-h1:mb-8
                                                prose-h2:text-2xl prose-h2:font-semibold prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-1 prose-h2:border-b prose-h2:border-border/40
                                                prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-4
                                                prose-p:leading-7 prose-p:my-5 prose-p:text-base
                                                prose-ul:my-6 prose-ul:list-disc prose-ul:ms-6
                                                prose-li:my-2
                                                prose-strong:font-semibold prose-strong:text-foreground
                                                prose-a:text-primary prose-a:underline prose-a:underline-offset-4 prose-a:decoration-primary/50 hover:prose-a:decoration-primary
                                                prose-img:rounded-lg prose-img:border prose-img:shadow-sm
                                                prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:bg-muted prose-code:text-foreground prose-code:font-mono prose-code:text-sm
                                            ">
                                {children}
                            </article>
                        </main>

                        {/* Right Sidebar: Table of Contents & CTA (Sticky) */}
                        <aside className="hidden xl:block w-64 shrink-0">
                            <div className="sticky top-28 flex flex-col gap-8 max-h-[calc(100vh-140px)] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                                {/* TOC - Scrollable if too long, limited height to save room for CTA */}
                                <div className="pl-4 rtl:pl-0 rtl:pr-4 border-l rtl:border-l-0 rtl:border-r border-border/40">
                                    <TableOfContents locale={locale} />
                                </div>

                                {/* CTA - Always visible below TOC */}
                                <div className="pl-4 rtl:pl-0 rtl:pr-4 shrink-0">
                                    <div className="rounded-xl bg-primary/5 border border-primary/10 p-4 relative overflow-hidden group">
                                        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl group-hover:bg-primary/20 transition-all duration-500" />

                                        <div className="flex items-start gap-3 mb-3 relative z-10">
                                            <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg 
                                                from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5">
                                                <Zap className="w-4 h-4 fill-current" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-xs leading-tight text-foreground">
                                                    {locale === 'ar' ? 'ابدأ رحلتك' : locale === 'fr' ? 'Lancez-vous' : 'Start Journey'}
                                                </h4>
                                                <p className="text-[10px] text-muted-foreground mt-0.5 font-medium">
                                                    {locale === 'ar' ? 'متجرك جاهز في دقائق' : locale === 'fr' ? 'Boutique en minutes' : 'Store in minutes'}
                                                </p>
                                            </div>
                                        </div>

                                        <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed relative z-10">
                                            {locale === 'ar'
                                                ? 'أنشئ متجرك الإلكتروني الاحترافي مع Ecommaps.'
                                                : locale === 'fr'
                                                    ? 'Créez votre boutique en ligne professionnelle avec Ecommaps.'
                                                    : 'Create your professional online store with Ecommaps.'}
                                        </p>

                                        <a href={getAuthUrl(locale, '/signup')} className={cn(
                                            buttonVariants({ size: "sm", variant: "default" }),
                                            "w-full h-8 text-xs font-semibold shadow-none from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5"
                                        )}>
                                            {locale === 'ar' ? 'ابدأ مجاناً' : locale === 'fr' ? 'Commencer' : 'Start for free'}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>

                <Footer />
            </div>
            {/* End of Floating Card */}
        </div >
    )
}
